import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "px-4 py-6" }
const _hoisted_2 = { class: "mb-4 flex items-center justify-between" }
const _hoisted_3 = {
  key: 0,
  class: "space-y-4"
}
const _hoisted_4 = {
  key: 0,
  class: "card !bg-emerald-50/50 !border-emerald-200"
}
const _hoisted_5 = { class: "flex items-start justify-between gap-4" }
const _hoisted_6 = { class: "kv !mb-0" }
const _hoisted_7 = { class: "kv-value text-lg" }
const _hoisted_8 = {
  key: 0,
  class: "rounded-full bg-emerald-500 px-4 py-1.5 text-base font-bold text-[#0a0d0f] shadow-sm"
}
const _hoisted_9 = {
  key: 1,
  class: "flex flex-col items-end text-sm"
}
const _hoisted_10 = { class: "line-through text-slate-500" }
const _hoisted_11 = { class: "rounded-full bg-emerald-500 px-4 py-1 flex items-center font-bold text-[#0a0d0f] shadow-sm" }
const _hoisted_12 = { class: "text-xs text-emerald-600 font-bold mt-1" }
const _hoisted_13 = { class: "card space-y-6" }
const _hoisted_14 = { class: "kv" }
const _hoisted_15 = { class: "kv-value text-base" }
const _hoisted_16 = { class: "kv" }
const _hoisted_17 = {
  key: 0,
  class: "kv"
}
const _hoisted_18 = { class: "kv-value text-base" }
const _hoisted_19 = {
  key: 1,
  class: "kv"
}
const _hoisted_20 = { class: "kv-value text-base text-emerald-600 font-bold" }
const _hoisted_21 = {
  key: 2,
  class: "mt-5 overflow-hidden rounded-xl border border-theme-gray-9"
}
const _hoisted_22 = { class: "mt-5 flex flex-wrap items-center gap-2" }
const _hoisted_23 = { class: "chip" }
const _hoisted_24 = { class: "chip" }
const _hoisted_25 = {
  key: 1,
  class: "chip"
}

import { onMounted, ref } from 'vue'
import { retrieveUserSearch } from '@/services/search-service'
import { UserSearch, Car as CarData } from '@/types'
import { getSessionPrefix } from '@/utils/common'
import GoogleMap from '@/components/GoogleMap.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'BookingSummary',
  props: {
    currentStep: {},
    couponDiscount: {},
    newTotal: {}
  },
  emits: ["distance-arrived"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const userSearch = ref<UserSearch | null>(null)
const selectedCar = ref<CarData | null>(null)

const distance = ref<string>('—')
const time = ref<string>('—')
const mapKey = ref<number>(0)

const getCategoryImage = (typeName: string) => {
  const name = (typeName || '').toLowerCase()
  if (name.includes('premium')) return '/assets/cars/e-class.png'
  if (name.includes('luxury')) return '/assets/cars/s-class.png'
  if (name.includes('van')) return '/assets/cars/v-class.png'
  if (name.includes('suv')) return '/assets/cars/suv.png'
  
  return '/assets/nocarfound.jpeg'
}

function handleMapData(value: string, type: string) {
  if (type === 'distance') {
     distance.value = value || '—'
     // Extract numeric value from string like "20.7 km"
     const numericValue = parseFloat((value || '0').replace(/[^\d.]/g, ''))
     emit('distance-arrived', numericValue)
  }
  else if (type === 'time') time.value = value || '—'
}

function handlePickUpClick() {
  const locationName = userSearch.value?.pickUp.location.name as string
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationName)}`
  window.open(url, '_blank')
}

onMounted(() => {
  userSearch.value = retrieveUserSearch()
  selectedCar.value = retrieveSelectedVehicle()

  if (userSearch.value?.type === 'distance') {
    // force map to remount so it recalculates for the *current* search
    mapKey.value++

    // Calculate fallback Haversine distance immediately (self-healing if Google Maps fails)
    try {
      const pLoc = userSearch.value?.pickUp?.location
      const dLoc = userSearch.value?.dropOff?.location
      if (pLoc?.coordinates?.lat && pLoc?.coordinates?.lng && dLoc?.coordinates?.lat && dLoc?.coordinates?.lng) {
        const lat1 = Number(pLoc.coordinates.lat)
        const lon1 = Number(pLoc.coordinates.lng)
        const lat2 = Number(dLoc.coordinates.lat)
        const lon2 = Number(dLoc.coordinates.lng)
        
        const R = 6371 // Radius of the earth in km
        const dLat = (lat2 - lat1) * Math.PI / 180
        const dLon = (lon2 - lon1) * Math.PI / 180
        const a = 
           Math.sin(dLat/2) * Math.sin(dLat/2) +
           Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
           Math.sin(dLon/2) * Math.sin(dLon/2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
        const d = R * c // Distance in km
        const estimatedKm = parseFloat((d * 1.3).toFixed(1)) // Multiply by 1.3 routing factor

        if (estimatedKm > 0) {
          distance.value = `${estimatedKm} km`
          time.value = `${Math.ceil(estimatedKm * 1.5)} mins`
          emit('distance-arrived', estimatedKm)
        }
      }
    } catch (e) {
      console.log('Error calculating fallback distance:', e)
    }
  } else {
    distance.value = '—'
    time.value = '—'
  }
})

function retrieveSelectedVehicle(): CarData | null {
  const prefix = getSessionPrefix()
  const raw = localStorage.getItem(`${prefix}_selected_car`)
  return raw ? JSON.parse(raw) : null
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[0] || (_cache[0] = _createElementVNode("h3", { class: "text-theme-gray-5 text-lg font-bold" }, "Summary", -1)),
      (userSearch.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["pill", userSearch.value.type === 'distance'
          ? 'pill-emerald'
          : 'pill-sky'])
          }, _toDisplayString(userSearch.value.type), 3))
        : _createCommentVNode("", true)
    ]),
    (userSearch.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (selectedCar.value && _ctx.currentStep?.uri === 'contact-details')
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "kv-label" }, "Vehicle selected", -1)),
                    _createElementVNode("div", _hoisted_7, _toDisplayString((Number(selectedCar.value.price || 0) <= 0) ? (selectedCar.value.type?.name || selectedCar.value.name) : selectedCar.value.name), 1)
                  ]),
                  (selectedCar.value.price && selectedCar.value.price > 0 && !_ctx.couponDiscount)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, " $" + _toDisplayString(selectedCar.value.price.toFixed(2)), 1))
                    : _createCommentVNode("", true),
                  (_ctx.couponDiscount && _ctx.newTotal !== undefined)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, "$" + _toDisplayString((selectedCar.value.price ?? 0).toFixed(2)), 1),
                        _createElementVNode("div", _hoisted_11, " $" + _toDisplayString(_ctx.newTotal.toFixed(2)), 1),
                        _createElementVNode("div", _hoisted_12, " Discount: -$" + _toDisplayString(_ctx.couponDiscount.toFixed(2)), 1)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              _cache[2] || (_cache[2] = _createElementVNode("div", { class: "kv-label" }, "Pickup date & time", -1)),
              _createElementVNode("div", _hoisted_15, _toDisplayString(userSearch.value.pickUp.date) + ", " + _toDisplayString(userSearch.value.pickUp.time), 1)
            ]),
            _createElementVNode("div", _hoisted_16, [
              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "kv-label" }, "Pickup location", -1)),
              _createElementVNode("button", {
                onClick: handlePickUpClick,
                class: "kv-value text-base text-left hover:underline"
              }, _toDisplayString(userSearch.value.pickUp.location.name), 1)
            ]),
            (userSearch.value.type === 'distance')
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  _cache[4] || (_cache[4] = _createElementVNode("div", { class: "kv-label" }, "Drop-off location", -1)),
                  _createElementVNode("div", _hoisted_18, _toDisplayString(userSearch.value.dropOff.location.name), 1)
                ]))
              : _createCommentVNode("", true),
            (userSearch.value.type === 'distance' && distance.value !== '—')
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                  _cache[5] || (_cache[5] = _createElementVNode("div", { class: "kv-label" }, "Distance & Duration", -1)),
                  _createElementVNode("div", _hoisted_20, _toDisplayString(distance.value) + " (" + _toDisplayString(time.value) + ") ", 1)
                ]))
              : _createCommentVNode("", true),
            (userSearch.value.type === 'distance' && userSearch.value.dropOff?.location?.id)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                  (_openBlock(), _createBlock(GoogleMap, {
                    key: mapKey.value,
                    origin: userSearch.value.pickUp.location.id,
                    destination: userSearch.value.dropOff.location.id,
                    driveType: userSearch.value.type,
                    onDistance: handleMapData,
                    onTime: handleMapData
                  }, null, 8, ["origin", "destination", "driveType"]))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_22, [
              (userSearch.value.type === 'distance')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("span", _hoisted_23, _toDisplayString(distance.value), 1),
                    _createElementVNode("span", _hoisted_24, _toDisplayString(time.value), 1)
                  ], 64))
                : (_openBlock(), _createElementBlock("span", _hoisted_25, _toDisplayString((userSearch.value.hours ?? 0) / 10) + " day(s)", 1))
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})